import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/feature/Navbar';
import Footer from '../../../../components/feature/Footer';

const TvmGuvenlikAcigiPage = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const CodeBlock = ({ title, code }: { title: string; code: string }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
      try {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Copy failed', err);
      }
    };
    return (
      <div className="bg-gray-900 rounded-xl p-6 overflow-x-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="text-gray-400 text-sm">{title}</span>
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            </div>
            <span>{copied ? 'Kopyalandı!' : 'Kopyala'}</span>
          </button>
        </div>
        <pre className="text-sm text-gray-100 whitespace-pre-wrap">{code}</pre>
      </div>
    );
  };

  const tvmCapabilities = [
    {
      icon: 'ri-radar-line',
      title: 'Sürekli Keşif',
      description:
        'Tüm uç noktalarda yüklü yazılımları, açık portları ve yapılandırmaları gerçek zamanlı olarak tarar ve envanter oluşturur.',
      color: 'from-[#0078D4] to-[#005A9E]',
    },
    {
      icon: 'ri-alert-line',
      title: 'Risk Önceliklendirme',
      description:
        'CVE skorlarını, tehdit istihbaratını ve varlık kritikliğini birleştirerek gerçek riski hesaplar ve önceliklendirir.',
      color: 'from-[#005A9E] to-[#003D6B]',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Güvenlik Önerileri',
      description:
        'Her güvenlik açığı için somut düzeltme adımları, etki analizi ve alternatif çözümler sunar.',
      color: 'from-[#00BCF2] to-[#0078D4]',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'İlerleme Takibi',
      description:
        'Düzeltme süreçlerini izler, SLA uyumunu ölçer ve yönetim raporları oluşturur.',
      color: 'from-[#003D6B] to-[#002050]',
    },
  ];

  const exposureMetrics = [
    {
      metric: 'Exposure Score',
      description: '0-1000 arası bir skor ile organizasyonunuzun genel güvenlik açığı durumunu gösterir',
      icon: 'ri-dashboard-line',
      color: 'bg-[#0078D4]',
      target: '&lt; 400',
    },
    {
      metric: 'Configuration Score',
      description: 'Güvenlik yapılandırmalarının (ASR, Firewall, vb.) ne kadar iyi uygulandığını ölçer',
      icon: 'ri-settings-3-line',
      color: 'bg-[#00BCF2]',
      target: '&gt; 80%',
    },
    {
      metric: 'Critical Assets',
      description: 'Yüksek değerli varlıkların (domain controller, veritabanı sunucuları) korunma durumu',
      icon: 'ri-server-line',
      color: 'bg-[#005A9E]',
      target: '100%',
    },
  ];

  const recommendationCategories = [
    {
      category: 'Devices - Vulnerabilities',
      description: 'Uç noktalardaki yazılım güvenlik açıkları (CVE)',
      icon: 'ri-bug-line',
      color: 'text-red-600 bg-red-50',
      examples: [
        'Microsoft Edge 120.0.2210.91 → 121.0.2277.4 güncellemesi',
        'Adobe Acrobat Reader CVE-2024-20767 yaması',
        'Windows 11 22H2 Cumulative Update KB5034123',
      ],
    },
    {
      category: 'Devices - Misconfigurations',
      description: 'Güvenlik yapılandırma hataları ve eksiklikleri',
      icon: 'ri-error-warning-line',
      color: 'text-amber-600 bg-amber-50',
      examples: [
        'BitLocker şifreleme etkin değil',
        'Windows Firewall kapalı',
        'ASR kuralları yapılandırılmamış',
      ],
    },
    {
      category: 'Cloud',
      description: 'Azure, AWS, GCP kaynaklarındaki güvenlik sorunları',
      icon: 'ri-cloud-line',
      color: 'text-[#0078D4] bg-[#0078D4]/10',
      examples: [
        'Azure VM\'de JIT erişim etkin değil',
        'S3 bucket public erişime açık',
        'NSG kuralları çok geniş',
      ],
    },
    {
      category: 'Identity',
      description: 'Kimlik tabanlı riskler ve zayıf noktalar',
      icon: 'ri-user-line',
      color: 'text-purple-600 bg-purple-50',
      examples: [
        'MFA etkin olmayan yönetici hesapları',
        'Süresi dolmayan parolalar',
        'Aşırı yetkili servis hesapları',
      ],
    },
  ];

  const remediationWorkflow = [
    {
      step: 1,
      title: 'Öneri Seçimi',
      description:
        'Exposure Management &gt; Recommendations sayfasından yüksek öncelikli bir öneri seçin.',
      icon: 'ri-file-list-3-line',
      color: 'bg-[#0078D4]',
    },
    {
      step: 2,
      title: 'Etki Analizi',
      description:
        'Kaç cihazın etkilendiğini, risk skorunu ve düzeltme süresini inceleyin.',
      icon: 'ri-pie-chart-line',
      color: 'bg-[#00BCF2]',
    },
    {
      step: 3,
      title: 'Düzeltme Planı',
      description:
        'Remediation sekmesinden düzeltme talebi oluşturun veya mevcut bir talebe ekleyin.',
      icon: 'ri-calendar-check-line',
      color: 'bg-[#005A9E]',
    },
    {
      step: 4,
      title: 'Uygulama',
      description:
        'Intune, SCCM veya manuel yöntemlerle güncelleme/yapılandırma değişikliğini uygulayın.',
      icon: 'ri-tools-line',
      color: 'bg-[#003D6B]',
    },
    {
      step: 5,
      title: 'Doğrulama',
      description:
        'TVM otomatik olarak cihazları yeniden tarar ve düzeltmenin başarılı olduğunu doğrular.',
      icon: 'ri-checkbox-circle-line',
      color: 'bg-green-600',
    },
  ];

  const faqs = [
    {
      q: 'TVM ile Microsoft Defender Vulnerability Management arasındaki fark nedir?',
      a: 'TVM (Threat and Vulnerability Management), Microsoft Defender Vulnerability Management ürününün eski adıdır. 2024-2025 döneminde Microsoft, bu özelliği "Exposure Management" şemsiyesi altına taşıdı. Artık security.microsoft.com portalında "Exposure management > Vulnerability management" yolundan erişilir. İşlevsellik aynı kaldı, sadece menü konumu ve isim değişti.',
    },
    {
      q: 'TVM için ayrı bir lisans gerekli mi?',
      a: 'Temel TVM özellikleri (yazılım envanteri, güvenlik açığı taraması, öneriler) Microsoft Defender for Endpoint Plan 2 lisansına dahildir. Ancak gelişmiş özellikler (sertifika yönetimi, tarayıcı uzantıları, ağ cihazları taraması) için "Defender Vulnerability Management Add-on" lisansı gerekir. Bulut kaynakları için Defender for Cloud entegrasyonu ayrıca değerlendirilmelidir.',
    },
    {
      q: 'TVM ne sıklıkla tarama yapar?',
      a: 'TVM, MDE sensörü üzerinden sürekli (continuous) tarama yapar; yeni bir yazılım yüklendiğinde veya güncelleme yapıldığında anında algılar. CVE veritabanı günlük olarak güncellenir. Ağ tabanlı taramalar (authenticated scan özelliği 18 Aralık 2025\'te deprecated edildi) yerine artık agent-based yaklaşım kullanılır.',
    },
    {
      q: 'Exposure Score nasıl hesaplanır?',
      a: 'Exposure Score, üç ana faktörü birleştirir: 1) Güvenlik açıklarının CVSS skorları, 2) Tehdit istihbaratı (exploit kitleri, aktif saldırılar), 3) Varlık kritikliği (domain controller, veritabanı sunucuları daha yüksek ağırlık alır). Skor 0-1000 arasındadır; 400\'ün altı iyi, 700\'ün üstü kritik kabul edilir.',
    },
    {
      q: 'TVM, üçüncü parti yazılımları da tarayabilir mi?',
      a: 'Evet. TVM, Windows üzerinde yüklü tüm yazılımları (Microsoft ve üçüncü parti) tarar. Adobe, Java, Chrome, Firefox, Zoom gibi yaygın uygulamaların güvenlik açıklarını tespit eder. Ayrıca 2025 sonunda eklenen "Cloud Software Inventory" özelliği ile Azure, AWS, GCP üzerindeki yazılımları da izleyebilirsiniz.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0078D4] to-[#005A9E] overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/edr"
              className="inline-flex items-center space-x-2 text-white/80 hover:text-white text-sm font-medium mb-8 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span>EDR Sayfasına Dön</span>
            </Link>

            <div className="flex items-center space-x-3 mb-6 flex-wrap gap-y-2">
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-md text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Güvenlik Açığı Yönetimi
              </span>
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-md text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Exposure Management
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Threat and Vulnerability Management ile Güvenlik Açığı Yönetimi
            </h1>

            <p className="text-lg text-white/85 leading-relaxed mb-8 max-w-3xl">
              Microsoft Defender Vulnerability Management (eski adıyla TVM) ile organizasyonunuzdaki
              güvenlik açıklarını keşfedin, risk bazlı önceliklendirin ve düzeltme süreçlerini
              merkezi olarak yönetin.
            </p>

            <div className="flex items-center space-x-6 text-white/70 text-sm flex-wrap gap-y-2">
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-line text-base"></i>
                </div>
                <span>12 Ocak 2026</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-base"></i>
                </div>
                <span>12 dk okuma</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-line text-base"></i>
                </div>
                <span>Özgür Çebi</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Makale İçeriği */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-8">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="text-gray-700 leading-relaxed space-y-6">
              {/* Giriş */}
              <p className="text-lg">
                Her gün yüzlerce yeni güvenlik açığı (CVE) yayınlanıyor. Ancak hangi açıklar gerçekten
                kritik? Hangileri aktif olarak sömürülüyor? Hangi cihazlar en riskli? Microsoft Defender
                Vulnerability Management (eski adıyla <strong>Threat and Vulnerability Management - TVM</strong>),
                bu soruları yanıtlayan ve güvenlik açığı yönetimini otomatikleştiren bir platformdur.
                2025 itibarıyla <strong>Exposure Management</strong> şemsiyesi altında yeniden yapılandırılan
                bu özellik, artık sadece uç noktaları değil, bulut kaynaklarını ve kimlik risklerini de
                tek bir yerden yönetmenizi sağlıyor.
              </p>

              {/* 2025 Güncellemesi */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-r-xl p-6 my-8">
                <div className="flex items-start">
                  <i className="ri-information-line text-2xl text-amber-600 mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">2025-2026 Önemli Değişiklikler</h4>
                    <ul className="text-gray-700 text-sm space-y-2">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span>
                          <strong>Yeni Portal Konumu:</strong> Artık <code className="bg-white px-2 py-0.5 rounded text-xs">security.microsoft.com &gt; Exposure management &gt; Vulnerability management</code> yolundan erişiliyor
                        </span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span>
                          <strong>Unified Recommendations:</strong> Defender for Endpoint, Defender for Cloud ve Identity önerileri tek bir katalogda birleştirildi
                        </span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span>
                          <strong>Deprecated:</strong> Windows Authenticated Scan özelliği 18 Aralık 2025'te kaldırıldı; artık tamamen agent-based tarama kullanılıyor
                        </span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5 flex-shrink-0"></i>
                        <span>
                          <strong>Yeni:</strong> "Software components" sayfası (eski adı "Vulnerable components") Şubat 2026'da yeniden adlandırıldı
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* TVM Nedir? */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">TVM Nedir ve Nasıl Çalışır?</h2>
                <p className="mb-6">
                  Microsoft Defender Vulnerability Management, MDE sensörü üzerinden çalışan, aracı tabanlı
                  (agent-based) bir güvenlik açığı yönetim platformudur. Geleneksel zafiyet tarayıcılarından
                  (Nessus, Qualys) farklı olarak, ağ taraması yapmaz; bunun yerine her cihaza yüklü MDE
                  aracısı üzerinden sürekli veri toplar.
                </p>

                <div className="grid md:grid-cols-2 gap-6 my-8">
                  {tvmCapabilities.map((cap, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className={`w-14 h-14 bg-gradient-to-br ${cap.color} rounded-xl flex items-center justify-center mb-4`}>
                        <i className={`${cap.icon} text-3xl text-white`}></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{cap.title}</h3>
                      <p className="text-sm text-gray-600">{cap.description}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Geleneksel Tarayıcı vs. TVM</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-5 border border-gray-200">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                          <i className="ri-radar-line text-gray-600 text-lg"></i>
                        </div>
                        <h4 className="font-bold text-gray-900">Geleneksel Tarayıcı</h4>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-start">
                          <i className="ri-time-line text-gray-400 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Haftalık/aylık tarama</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-network-line text-gray-400 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Ağ tabanlı, port taraması</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-file-list-line text-gray-400 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Statik raporlar</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-error-warning-line text-gray-400 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Yüksek false positive</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-[#0078D4]">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-[#0078D4]/10 rounded-lg flex items-center justify-center mr-3">
                          <i className="ri-shield-check-line text-[#0078D4] text-lg"></i>
                        </div>
                        <h4 className="font-bold text-gray-900">TVM (Agent-Based)</h4>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-start">
                          <i className="ri-flashlight-line text-[#0078D4] mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Sürekli (continuous) tarama</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-cpu-line text-[#0078D4] mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Agent-based, doğrudan cihaz verisi</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-line-chart-line text-[#0078D4] mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Gerçek zamanlı dashboard</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-check-double-line text-[#0078D4] mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>Düşük false positive</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Exposure Management Entegrasyonu */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Exposure Management: Birleşik Görünüm</h2>
                <p className="mb-6">
                  2024-2025 döneminde Microsoft, TVM'yi daha geniş bir platform olan{' '}
                  <strong>Security Exposure Management</strong> içine entegre etti. Bu değişiklik,
                  sadece uç nokta güvenlik açıklarını değil, bulut yanlış yapılandırmalarını, kimlik
                  risklerini ve saldırı yollarını (attack paths) tek bir yerden görmenizi sağlıyor.
                </p>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Exposure Management Metrikleri</h3>
                  <div className="space-y-4">
                    {exposureMetrics.map((metric, index) => (
                      <div key={index} className="flex items-start">
                        <div className={`w-12 h-12 ${metric.color} text-white rounded-xl flex items-center justify-center mr-4 flex-shrink-0`}>
                          <i className={`${metric.icon} text-2xl`}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-bold text-gray-900">{metric.metric}</h4>
                            <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                              Hedef: {metric.target}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{metric.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-lightbulb-line text-2xl text-blue-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Attack Path Analysis</h4>
                      <p className="text-gray-700 text-sm">
                        Exposure Management'ın en güçlü özelliklerinden biri, saldırganların hangi
                        yolları kullanarak kritik varlıklarınıza ulaşabileceğini görselleştirmesidir.
                        Örneğin: "Yama yapılmamış kullanıcı bilgisayarı → Credential theft → Domain
                        Admin hesabı → Domain Controller" gibi bir saldırı zincirini otomatik tespit
                        eder ve hangi düzeltmenin bu zinciri kıracağını önerir.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Unified Recommendations */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Unified Recommendations Catalog</h2>
                <p className="mb-6">
                  2025 itibarıyla, güvenlik önerileri artık dört ana kategoride birleştirildi. Bu,
                  farklı ekiplerin (endpoint, cloud, identity) aynı platformdan çalışmasını sağlıyor.
                </p>

                <div className="space-y-6">
                  {recommendationCategories.map((cat, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className="flex items-start">
                        <div className={`w-12 h-12 ${cat.color} rounded-xl flex items-center justify-center mr-4 flex-shrink-0`}>
                          <i className={`${cat.icon} text-2xl`}></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{cat.category}</h3>
                          <p className="text-sm text-gray-600 mb-4">{cat.description}</p>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                              Örnek Öneriler
                            </h4>
                            <ul className="space-y-1.5">
                              {cat.examples.map((example, ei) => (
                                <li key={ei} className="text-sm text-gray-600 flex items-start">
                                  <i className="ri-arrow-right-s-line text-[#0078D4] mr-1.5 mt-0.5 flex-shrink-0"></i>
                                  <span>{example}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Düzeltme Süresi */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Düzeltme Süresi: Adım Adım</h2>
                <p className="mb-8">
                  TVM, sadece güvenlik açıklarını listelemekle kalmaz; düzeltme sürecini de yönetir.
                  İşte tipik bir düzeltme akışı:
                </p>

                <div className="space-y-6">
                  {remediationWorkflow.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex flex-col items-center mr-6">
                        <div className={`w-14 h-14 ${item.color} text-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                          <i className={`${item.icon} text-2xl`}></i>
                        </div>
                        {index < remediationWorkflow.length - 1 && (
                          <div className="w-0.5 h-12 bg-gray-200 mt-2" />
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            Adım {item.step}
                          </span>
                          <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                        </div>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 mt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Intune Entegrasyonu</h3>
                  <p className="text-gray-700 text-sm mb-4">
                    TVM, Microsoft Intune ile doğrudan entegre çalışır. Bir güvenlik açığı için düzeltme
                    talebi oluşturduğunuzda, Intune otomatik olarak ilgili cihazlara güncelleme politikası
                    dağıtabilir. Bu, manuel müdahaleyi minimuma indirir.
                  </p>
                  <div className="bg-white rounded-xl p-5 border border-green-200">
                    <h4 className="font-semibold text-gray-900 mb-3 text-sm">Örnek Senaryo</h4>
                    <ol className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">1.</span>
                        <span>TVM, 150 cihazda Adobe Acrobat Reader CVE-2024-20767 tespit eder</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">2.</span>
                        <span>Analist, "Create remediation request" butonuna tıklar</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">3.</span>
                        <span>Intune, otomatik olarak Adobe Reader 24.001.20643 güncellemesini dağıtır</span>
                      </li>
                      <li className="flex items-start">
                        <span className="font-bold text-green-600 mr-2">4.</span>
                        <span>TVM, 24 saat içinde cihazları yeniden tarar ve düzeltmeyi doğrular</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </section>

              {/* KQL Sorguları */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Hunting ile TVM Analizi</h2>
                <p className="mb-6">
                  TVM verilerini Advanced Hunting üzerinden KQL sorguları ile derinlemesine analiz edebilirsiniz.
                </p>

                <div className="space-y-6">
                  <CodeBlock
                    title="KQL - En Kritik Güvenlik Açıkları"
                    code={`// CVSS skoru 9.0 ve üzeri olan, aktif exploit'i bilinen CVE'ler
DeviceTvmSoftwareVulnerabilities
| where CvssScore >= 9.0
| where IsExploitAvailable == true
| summarize
    EtkilenenCihaz = dcount(DeviceName),
    IlkGorulenTarih = min(FirstSeenTimestamp)
    by CveId, VulnerabilitySeverityLevel
| sort by EtkilenenCihaz desc
| take 20`}
                  />

                  <CodeBlock
                    title="KQL - Yazılım Envanteri"
                    code={`// Organizasyonda en yaygın kullanılan yazılımlar ve sürümleri
DeviceTvmSoftwareInventory
| summarize
    CihazSayisi = dcount(DeviceName),
    Surum = make_set(SoftwareVersion)
    by SoftwareName, SoftwareVendor
| where CihazSayisi > 10
| sort by CihazSayisi desc
| take 50`}
                  />

                  <CodeBlock
                    title="KQL - Düzeltme İlerlemesi"
                    code={`// Son 30 günde düzeltilen güvenlik açıkları
DeviceTvmSoftwareVulnerabilitiesTimeline
| where Timestamp > ago(30d)
| where EventType == "VulnerabilityRemediated"
| summarize
    DuzeltilenCVE = dcount(CveId),
    EtkilenenCihaz = dcount(DeviceName)
    by bin(Timestamp, 1d)
| render timechart`}
                  />

                  <CodeBlock
                    title="KQL - Kritik Varlıklardaki Açıklar"
                    code={`// Domain Controller ve veritabanı sunucularındaki güvenlik açıkları
let CriticalDevices = DeviceInfo
    | where DeviceType in ("DomainController", "Server")
    | where DeviceName contains "SQL" or DeviceName contains "DC"
    | distinct DeviceName;
DeviceTvmSoftwareVulnerabilities
| where DeviceName in (CriticalDevices)
| where CvssScore >= 7.0
| project
    DeviceName,
    CveId,
    CvssScore,
    SoftwareName,
    SoftwareVersion,
    RecommendedSecurityUpdate
| sort by CvssScore desc`}
                  />
                </div>
              </section>

              {/* Sık Sorulan Sorular */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Sık Sorulan Sorular</h2>
                <div className="space-y-3">
                  {faqs.map((faq, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-gray-500 text-xl transition-transform`}></i>
                        </div>
                      </button>
                      {expandedFaq === index && (
                        <div className="px-5 pb-5 border-t border-gray-100">
                          <p className="text-sm text-gray-600 leading-relaxed pt-4">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Sonuç */}
              <section className="mb-12">
                <div className="bg-gradient-to-br from-[#0078D4] to-[#005A9E] text-white rounded-2xl p-8">
                  <h2 className="text-2xl font-bold mb-4">Özet ve Sonraki Adımlar</h2>
                  <p className="text-white/90 mb-6">
                    Microsoft Defender Vulnerability Management (TVM), modern güvenlik açığı yönetiminin
                    temel taşıdır. 2025-2026 güncellemeleriyle Exposure Management platformuna entegre
                    edilen TVM, artık sadece uç noktaları değil, bulut ve kimlik risklerini de tek bir
                    yerden yönetmenizi sağlıyor. Başarılı bir TVM dağıtımı için: MDE sensörünün tüm
                    cihazlarda aktif olduğundan emin olun, Exposure Score'u düzenli izleyin, kritik
                    varlıklar için özel etiketler oluşturun ve Intune entegrasyonunu kullanarak düzeltme
                    süreçlerini otomatikleştirin.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">Sürekli</div>
                      <div className="text-white/80 text-sm">Agent-Based Tarama</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">4 Kategori</div>
                      <div className="text-white/80 text-sm">Unified Recommendations</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">&lt; 400</div>
                      <div className="text-white/80 text-sm">Hedef Exposure Score</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* İlgili Makaleler */}
              <section className="mt-16 pt-12 border-t-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">İlgili Makaleler</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <Link
                    to="/edr/defender-for-endpoint"
                    className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0078D4] transition-all cursor-pointer"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0078D4] transition-colors">
                      Microsoft Defender for Endpoint Rehberi
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      TVM'nin çalıştığı temel platform olan MDE'nin tüm yeteneklerini keşfedin
                    </p>
                    <span className="text-[#0078D4] text-sm font-medium">
                      Devamını Oku <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </Link>

                  <Link
                    to="/edr/air-otomatik-yanit"
                    className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0078D4] transition-all cursor-pointer"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0078D4] transition-colors">
                      Otomatik Soruşturma ve Yanıt (AIR)
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      TVM'nin tespit ettiği güvenlik açıklarını sömüren tehditlere otomatik yanıt verin
                    </p>
                    <span className="text-[#0078D4] text-sm font-medium">
                      Devamını Oku <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </Link>
                </div>
              </section>
            </div>
          </motion.article>
        </div>
      </section>

      {/* Alt Navigasyon */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8">
          <div className="flex items-center justify-between">
            <Link
              to="/edr"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer text-gray-700 hover:text-[#0078D4]"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="text-sm font-medium whitespace-nowrap">EDR Sayfası</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer text-gray-700 hover:text-[#0078D4]"
            >
              <span className="text-sm font-medium whitespace-nowrap">Ana Sayfa</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TvmGuvenlikAcigiPage;
